import React from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate, useLocation} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { DomainProvider } from './contexts/DomainContext';
import './config/axios'; // Import axios configuration
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import DashboardLayout from './components/Layout/DashboardLayout';
import SERPfinityHome from './pages/SERPfinityHome';
import TokenHandler from './pages/TokenHandler';
import Home from './pages/Home';
import ScanResults from './pages/ScanResults';
import UserDashboard from './pages/UserDashboard';
import Register from './pages/Register';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import Pricing from './pages/Pricing';
import Profile from './pages/Profile';
import ContentWriter from './pages/ContentWriter';
import KeywordResearch from './pages/KeywordResearch';
import DomainAnalysis from './pages/DomainAnalysis';
import RankTracking from './pages/RankTracking';
import CompetitiveAnalysis from './pages/CompetitiveAnalysis';
import BacklinkExplorer from './pages/BacklinkExplorer';
import SiteAudit from './pages/SiteAudit';
import AISearchVisibilityPage from './pages/AISearchVisibilityPage';
import SERPAnalysisPage from './pages/SERPAnalysisPage';
import ListingManagement from './pages/ListingManagement';
import LocalRankings from './pages/LocalRankings';
import ContentOptimizationPage from './pages/ContentOptimizationPage';
import TopicResearch from './pages/TopicResearch';
import ContentAudit from "./pages/ContentAudit";
import Integrations from './pages/Integrations';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import ContactUs from './pages/ContactUs';
import HelpCenter from './pages/HelpCenter';
import Features from './pages/Features';
import AmbassadorProgram from './pages/AmbassadorProgram';
import Projects from './pages/Projects';
import MyReports from './pages/MyReports';
import SubscriptionExpired from './pages/SubscriptionExpired';
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import ReviewManagement from "./pages/ReviewManagement";
import GEODashboard from './pages/GEODashboard';
import GEOTracking from './pages/GEOTracking';
import GEOAIPrompts from './pages/GEOAIPrompts';
import TrialBanner from './components/TrialBanner';
import Settings from './pages/Settings';
import SocialMediaInsights from './pages/SocialMediaInsights';
import './App.css';


function AppContent() {
  const location = useLocation();
  const publicPaths = ['/', '/classic', '/login', '/register', '/forgot-password', '/reset-password', '/pricing', '/privacy', '/terms', '/contact', '/help', '/features', '/ambassador', '/projects', '/my-reports', '/subscription-expired', '/subscription-success', '/auth/token'];
  const isPublicPage = publicPaths.includes(location.pathname);

  return (
    <>
      <ScrollToTop />
      <div className={isPublicPage ? "flex flex-col min-h-screen" : "min-h-screen"}>
        {isPublicPage && <Navbar />}
        {isPublicPage && <TrialBanner />}
        <main className="flex-grow">
      <Routes>
      {/* Public routes */}
      <Route path="/auth/token" element={<TokenHandler />} />
      <Route path="/" element={<SERPfinityHome />} />
      <Route path="/classic" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/features" element={<Features />} />
      <Route path="/ambassador" element={<AmbassadorProgram />} />
      <Route path="/projects" element={<Projects />} />
      <Route path="/my-reports" element={<MyReports />} />
      <Route path="/subscription-expired" element={<SubscriptionExpired />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      
      {/* Protected service routes - require login */}
      <Route path="/results/:id" element={
      <ProtectedRoute>
      <DashboardLayout>
      <ScanResults />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/analyze" element={
      <ProtectedRoute>
      <DomainAnalysis />
      </ProtectedRoute>
      } />
      
      {/* AI-Powered Features */}
      <Route path="/ai-search-visibility" element={
      <ProtectedRoute>
      <DashboardLayout>
      <AISearchVisibilityPage />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/geo" element={
      <ProtectedRoute>
      <DashboardLayout>
      <GEODashboard />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/geo/tracking" element={
      <ProtectedRoute>
      <DashboardLayout>
      <GEOTracking />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/geo/ai-prompts" element={
      <ProtectedRoute>
      <DashboardLayout>
      <GEOAIPrompts />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/serp-analysis" element={
      <ProtectedRoute>
      <DashboardLayout>
      <SERPAnalysisPage />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/content-optimization" element={
      <ProtectedRoute>
      <DashboardLayout>
      <ContentOptimizationPage />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/content-analyzer" element={
      <ProtectedRoute>
      <DashboardLayout>
      <ContentOptimizationPage />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/topic-research" element={
      <ProtectedRoute>
      <DashboardLayout>
      <TopicResearch />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/content-audit" element={
      <ProtectedRoute>
      <DashboardLayout>
      <ContentAudit />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/local-rankings" element={
      <ProtectedRoute>
      <DashboardLayout>
      <LocalRankings />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/listing-management" element={
      <ProtectedRoute>
      <DashboardLayout>
      <ListingManagement />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      
      {/* Traditional SEO Tools */}
      <Route path="/content-writer" element={
      <ProtectedRoute>
      <DashboardLayout>
      <ContentWriter />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/keyword-research" element={
      <ProtectedRoute>
      <KeywordResearch />
      </ProtectedRoute>
      } />
      <Route path="/domain-analysis" element={
      <ProtectedRoute>
      <DomainAnalysis />
      </ProtectedRoute>
      } />
      <Route path="/rank-tracking" element={
      <ProtectedRoute>
      <DashboardLayout>
      <RankTracking />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/competitive-analysis" element={
      <ProtectedRoute>
      <DashboardLayout>
      <CompetitiveAnalysis />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/backlink-explorer" element={
      <ProtectedRoute>
      <DashboardLayout>
      <BacklinkExplorer />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/site-audit" element={
      <ProtectedRoute>
      <DashboardLayout>
      <SiteAudit />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      
      {/* Protected user routes */}
      <Route path="/dashboard" element={
      <ProtectedRoute>
      <UserDashboard />
      </ProtectedRoute>
      } />
      <Route path="/profile" element={
      <ProtectedRoute>
      <DashboardLayout>
      <Profile />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/integrations" element={
      <ProtectedRoute>
      <DashboardLayout>
      <Integrations />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      <Route path="/review-management" element={
      <ProtectedRoute>
      <DashboardLayout>
      <ReviewManagement />
      </DashboardLayout>
      </ProtectedRoute>
      } />
      
      <Route path="/upgrade" element={<Navigate to="/pricing" replace />} />
      <Route path="/reports" element={<ProtectedRoute><DashboardLayout><MyReports /></DashboardLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><DashboardLayout><Settings /></DashboardLayout></ProtectedRoute>} />
            <Route path="/social-media-insights" element={<ProtectedRoute><DashboardLayout><SocialMediaInsights /></DashboardLayout></ProtectedRoute>} />
      </Routes>
        </main>
        {isPublicPage && <Footer />}
      </div>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-gray-50">
      <AuthProvider>
        <DomainProvider>
        <Router>
          <AppContent />
        </Router>
      </DomainProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
